import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { TextInput } from '@doxyme/ui';
import { postcodeValidator, postcodeValidatorExistsForCountry, } from 'postcode-validator';
import classnames from 'classnames';
import './sales-tax-modal.scss';
import CountrySelect from '../country-select';
import { ProgressButton } from '../progress-button';
import { API } from '../../../../vendor/api';
import { selectLocationDetails, selectUser, } from '../../../../store/features/system';
import { actions as coreActions, ToastType } from '../../index';
import { getTabbableElements } from '../../helpers/get-tabbable-elements';
import { getPlanName } from '../price';
export var POPUP_TYPES;
(function (POPUP_TYPES) {
    POPUP_TYPES["BOTTOM_RIGHT"] = "bottom_right";
})(POPUP_TYPES || (POPUP_TYPES = {}));
const CONTAINER_CLASSES = {
    [POPUP_TYPES.BOTTOM_RIGHT]: 'gl-popup__bottom-right',
};
export const SalesTaxModal = (props) => {
    const { type = POPUP_TYPES.BOTTOM_RIGHT, imageLink, customerId } = props;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const locationDetails = useSelector(selectLocationDetails);
    const userDetails = useSelector(selectUser);
    const userLocation = locationDetails.location.country_code || 'US';
    const [showModal, setShowModal] = useState(true);
    const [postalCode, setPostalCode] = useState('');
    const [formError, setFormError] = useState('');
    const [country, setCountry] = useState(userLocation);
    const [isLoading, setIsLoading] = useState(false);
    const usesPostalCodes = useMemo(() => postcodeValidatorExistsForCountry(country), [country]);
    const isValidPostalCode = useMemo(() => (usesPostalCodes ? postcodeValidator(postalCode, country) : false), [usesPostalCodes, postalCode, country]);
    const disableSubmitButton = isLoading || (usesPostalCodes ? !postalCode || !isValidPostalCode : false);
    useEffect(() => {
        if (!usesPostalCodes) {
            setPostalCode('');
        }
    }, [usesPostalCodes]);
    const handleSubmit = useCallback(async () => {
        setFormError('');
        setIsLoading(true);
        try {
            // modal is shown only if the user has customerId
            // todo: rename planId after stripe reconfiguration, should be only active planId
            // planId is not required here because we check if a location is recognized
            const updatedCustomer = await API.stripe.updateCustomerCountryAndZip({
                customerId,
                email: userDetails.email,
                planId: getPlanName('pro_day_pass-v2', userDetails.currency),
                options: {
                    address: {
                        country,
                        ...(postalCode.length > 0 && { postal_code: postalCode }),
                    },
                },
            });
            if (updatedCustomer != null) {
                setIsLoading(false);
                localStorage.setItem('customerHasPostalAndCountryCode', 'true');
                // this timeout is purely so the user can observe the "success" display of the button
                setTimeout(() => {
                    setShowModal(false);
                }, 1500);
            }
        }
        catch (error) {
            let errorMessage = error.message;
            if (errorMessage === 'Unrecognized postal code') {
                errorMessage = formatMessage({
                    id: 'common.tax-error',
                    defaultMessage: 'Your Zip/Postal Code does not match the credit card issuing country.',
                });
            }
            dispatch(coreActions.addToast(ToastType.error, errorMessage));
            setFormError(errorMessage);
            setIsLoading(false);
        }
    }, [customerId, postalCode, userDetails, country, dispatch, formatMessage]);
    const handleSetPostalCode = useCallback((event) => {
        setPostalCode(event.target.value);
    }, []);
    const dialogRef = useRef(null);
    const previousFocusRef = useRef(null);
    /* eslint-disable consistent-return */
    useEffect(() => {
        if (!showModal) {
            return;
        }
        previousFocusRef.current = document.activeElement;
        const dialog = dialogRef.current;
        const frame = requestAnimationFrame(() => {
            if (!dialog) {
                return;
            }
            const tabbable = getTabbableElements(dialog);
            if (tabbable.length > 0) {
                tabbable[0].focus();
            }
            else {
                dialog.focus();
            }
        });
        return () => {
            cancelAnimationFrame(frame);
            const prev = previousFocusRef.current;
            if (prev && typeof prev.focus === 'function') {
                prev.focus();
            }
        };
    }, [showModal]);
    /* eslint-enable consistent-return */
    const handleDialogKeyDown = useCallback((event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setShowModal(false);
            return;
        }
        if (event.key !== 'Tab') {
            return;
        }
        const container = dialogRef.current;
        if (!container) {
            return;
        }
        const tabbable = getTabbableElements(container);
        if (tabbable.length === 0) {
            event.preventDefault();
            return;
        }
        const first = tabbable[0];
        const last = tabbable[tabbable.length - 1];
        const active = document.activeElement;
        if (!active || !container.contains(active)) {
            event.preventDefault();
            first.focus();
            return;
        }
        if (active === container) {
            event.preventDefault();
            if (event.shiftKey) {
                last.focus();
            }
            else {
                first.focus();
            }
            return;
        }
        if (event.shiftKey) {
            if (active === first) {
                event.preventDefault();
                last.focus();
            }
        }
        else if (active === last) {
            event.preventDefault();
            first.focus();
        }
    }, []);
    if (!showModal) {
        return null;
    }
    return (<div ref={dialogRef} className={classnames('gl-popup', CONTAINER_CLASSES[type])} role="dialog" aria-modal="true" aria-labelledby="sales-tax-modal-title" tabIndex={-1} onKeyDown={handleDialogKeyDown}>
      <div className="sales-tax-modal__close-container">
        <button type="button" data-test-id="sales-tax-modal-close-btn" className="sales-tax-modal__close-button" aria-label={formatMessage({
            id: 'common.close',
            defaultMessage: 'Close',
        })} onClick={() => setShowModal(false)}>
          <i className="icon icon-close" aria-hidden="true"/>
        </button>
      </div>
      {Boolean(imageLink) && (<img src={imageLink} alt="popup image" data-test-id="image-popup"/>)}
      <div className="gl-popup__message" data-test-id="message-popup">
        <p id="sales-tax-modal-title">
          <FormattedMessage id="account.sales-tax-modal" values={{
            FAQLink: (<a target="_blank" className="sales-tax-modal__popup-text--line-height" href="https://help.doxy.me/en/articles/6226116-sales-tax-faq" rel="noreferrer">
                  <FormattedMessage id="common.learnMore" defaultMessage="Learn more"/>
                </a>),
        }}/>
        </p>
      </div>
      <div className="sales-tax-modal__input-container">
        <CountrySelect data-test-id="sales-tax-modal-country-select" countryCode={userLocation} handleChange={setCountry}/>
        {usesPostalCodes && (<div>
            <label className="sales-tax-modal__postal-code-label ui-label" htmlFor="sales-tax-modal-postal-code">
              <FormattedMessage id="account.sales-tax-modal.postal-code-label" defaultMessage="Zip/Postal Code"/>
            </label>
            <TextInput id="sales-tax-modal-postal-code" name="postal-code" data-test-id="sales-tax-modal-postal-code-input" onChange={handleSetPostalCode} value={postalCode} hasError={postalCode.length > 0 ? !isValidPostalCode : false} errorText={formatMessage({
                id: 'account.sales-tax-modal.postal-code-validation',
                defaultMessage: 'Please enter a valid zip/postal code for your region.',
            })}/>
          </div>)}
      </div>
      <ProgressButton data-test-id="sales-tax-modal-submit-btn" className="gl-popup__dismiss btn btn-primary progress-button space-md-bottom" onClick={() => void handleSubmit()} disabled={disableSubmitButton} type="submit" isLoading={isLoading} hasError={Boolean(formError)}>
        <FormattedMessage id="common.update" defaultMessage="Update"/>
      </ProgressButton>
    </div>);
};
